import { useEffect, useRef } from 'react';

/**
 * Matches every control the form has flagged as invalid for assistive tech.
 *
 * Reading the DOM instead of a form library's error object is deliberate: `aria-invalid`
 * is the ONE place where both kinds of failure meet. react-hook-form only knows about the
 * errors it raised itself during client-side validation, while every server-side failure in
 * this app (wrong password, email already in use, password policy rejection, SSO not
 * enabled...) lives in a page-local `errorMessage` state that is fed into a field's
 * `isDanger` prop. A DOM query covers both with one rule.
 *
 * `querySelectorAll` returns matches in DOCUMENT order, which is exactly the order SC 3.3.1
 * expects us to walk — so nested sub-forms (address, full name, custom profile fields) are
 * handled for free, with no field registry to keep in sync with the layout.
 *
 * `[hidden]` and `[disabled]` can never take focus, so they are filtered out up front. The
 * identifier mirror inputs that some password forms render for password managers are
 * `hidden`, and must never become the focus target.
 */
const invalidFieldSelector = '[aria-invalid="true"]:not([hidden]):not([disabled])';

/**
 * Moves keyboard focus to the first invalid field once a submit attempt has FINISHED.
 *
 * Why this exists: the ARIA plumbing already tells a screen reader *that* something failed
 * (`aria-invalid` on the field, `aria-errormessage` pointing at the text, and a `role="alert"`
 * message that announces on insertion), but focus stays parked on the submit button. A
 * sighted mouse user sees the red field instantly; a keyboard or screen-reader user hears
 * "there is a problem" and then has to hunt back up the form for it. Moving focus is the
 * remedy WCAG expects (SC 3.3.1 Error Identification, SC 2.4.3 Focus Order).
 *
 * Usage: pass react-hook-form's `isSubmitting` and spread the returned ref onto the `<form>`.
 *
 * ```tsx
 * const formRef = useFocusFirstInvalidField(isSubmitting);
 * return <form ref={formRef} onSubmit={onSubmitHandler}>...</form>;
 * ```
 *
 * Design notes:
 *
 * - **Trigger.** We fire on the FALLING edge of `isSubmitting`, not on an error value
 *   changing. That is the only signal that works for both failure kinds: a client-side
 *   validation error and a server rejection both flip `isSubmitting` back to `false`, and
 *   both have committed their error state to the DOM by the time this effect runs (effects
 *   run after commit). Keying off the error text instead would miss the case where the user
 *   retries and gets the SAME message back — focus must still move.
 * - **Never steals focus on success.** A successful submit leaves no `aria-invalid="true"`
 *   control behind, so the query comes back empty and nothing is touched.
 * - **A loading button keeps focus.** While `isSubmitting` is `true` this does nothing at
 *   all, so the submit button — which stays focusable during loading by design, see
 *   `Button` — is never yanked out from under the user mid-request.
 * - **No double announcement.** This moves focus and nothing else: no extra live region, no
 *   re-rendered alert, no `alert()`. The `role="alert"` message keeps its single
 *   announcement-on-insertion, and the field announces its own name + invalid state +
 *   `aria-errormessage` when focus lands, which is the normal, expected pairing.
 * - **Yields if focus is already right.** If the user (or react-hook-form's own
 *   `shouldFocusError`, which we deliberately leave enabled as a belt-and-braces path for
 *   the fields it can reach) already put focus on an invalid field inside this form, we
 *   leave it alone rather than re-focusing and provoking a second announcement.
 */
const useFocusFirstInvalidField = (isSubmitting: boolean) => {
  const formRef = useRef<HTMLFormElement>(null);
  const wasSubmittingRef = useRef(false);

  useEffect(() => {
    const hasJustFinishedSubmitting = wasSubmittingRef.current && !isSubmitting;
    // eslint-disable-next-line @silverhand/fp/no-mutation
    wasSubmittingRef.current = isSubmitting;

    if (!hasJustFinishedSubmitting) {
      return;
    }

    const form = formRef.current;

    if (!form) {
      return;
    }

    const { activeElement } = document;

    if (
      activeElement instanceof HTMLElement &&
      form.contains(activeElement) &&
      activeElement.matches(invalidFieldSelector)
    ) {
      return;
    }

    /**
     * Walk the candidates in document order and stop at the first one that actually TOOK
     * focus. A control can be marked invalid yet be unfocusable (a read-only mirror, or an
     * `aria-invalid` wrapper around a composite widget); skipping to the next candidate is
     * better than calling `focus()` into the void and leaving the user on the button.
     */
    for (const field of form.querySelectorAll<HTMLElement>(invalidFieldSelector)) {
      field.focus();

      if (document.activeElement === field) {
        return;
      }
    }
  }, [isSubmitting]);

  return formRef;
};

export default useFocusFirstInvalidField;
